import { useCallback, useEffect, useRef } from "react";
import type {
  NodeMouseDownContext,
  SegmentMouseDownContext,
} from "components/SmartSurveyor/ReviewPage/FloorPlanPanelNew/SvgOverlay/RoutesCreationLayer/types";
import type { UseNodeDraggingArgs, UseNodeDraggingResult } from "./types";

const DRAG_MOVEMENT_THRESHOLD_PX = 1;

// preventDefault keeps focus where it was, so a focused input would swallow the delete keys.
const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

interface ActiveDrag {
  routeIndex: number;
  nodeId: number;
  origin: { x: number; y: number };
  hasMoved: boolean;
}

export const useNodeDragging = ({
  isEditingActive,
  creationModeState,
}: UseNodeDraggingArgs): UseNodeDraggingResult => {
  const activeDragRef = useRef<ActiveDrag | null>(null);

  const { setSelectedNodeId } = creationModeState;

  const handleNodeMouseDown = useCallback(
    ({ routeId, nodeId, event }: NodeMouseDownContext) => {
      if (!isEditingActive) return;
      event.stopPropagation();
      event.preventDefault();
      blurActiveElement();

      const routeIndex = creationModeState.routes.findIndex(
        (row) => row.id === routeId,
      );
      if (routeIndex < 0) return;

      const node = creationModeState.routes[routeIndex].nodes.find(
        (candidate) => candidate.id === nodeId,
      );
      if (!node) return;

      // The node touched last is the selected one, so Delete works right after a drag.
      setSelectedNodeId(nodeId);
      activeDragRef.current = {
        routeIndex,
        nodeId,
        origin: { x: node.x, y: node.y },
        hasMoved: false,
      };
    },
    [isEditingActive, creationModeState, setSelectedNodeId],
  );

  const handleSegmentMouseDown = useCallback(
    ({ routeId, edgeIndex, event }: SegmentMouseDownContext) => {
      if (!isEditingActive) return;
      event.stopPropagation();
      event.preventDefault();
      blurActiveElement();

      const insertPos = creationModeState.hoverPosition;
      if (!insertPos) return;

      const routeIndex = creationModeState.routes.findIndex(
        (row) => row.id === routeId,
      );
      if (routeIndex < 0) return;

      const insertResult = creationModeState.insertNodeOnEdge({
        routeIndex,
        edgeIndex,
        pos: insertPos,
      });
      if (!insertResult) return;

      setSelectedNodeId(insertResult.newNodeId);
      activeDragRef.current = {
        routeIndex,
        nodeId: insertResult.newNodeId,
        origin: insertPos,
        hasMoved: false,
      };
    },
    [isEditingActive, creationModeState, setSelectedNodeId],
  );

  const applyDragPosition = useCallback(
    (pos: { x: number; y: number }) => {
      const drag = activeDragRef.current;
      if (!drag) return false;

      const distance = Math.hypot(pos.x - drag.origin.x, pos.y - drag.origin.y);
      if (distance > DRAG_MOVEMENT_THRESHOLD_PX || drag.hasMoved) {
        drag.hasMoved = true;
        creationModeState.setNodePosition({
          routeIndex: drag.routeIndex,
          nodeId: drag.nodeId,
          pos,
        });
      }
      return true;
    },
    [creationModeState],
  );

  const endDrag = useCallback(() => {
    activeDragRef.current = null;
  }, []);

  // One global mouseup listener ends the drag. Attached once on mount, removed on unmount.
  useEffect(() => {
    document.addEventListener("mouseup", endDrag);
    return () => document.removeEventListener("mouseup", endDrag);
  }, [endDrag]);

  return {
    handleNodeMouseDown,
    handleSegmentMouseDown,
    applyDragPosition,
    endDrag,
  };
};
